import type {
  AdminLog,
  AdminLogsQuery,
  AdminLogsResponse,
  AdminUser,
  AppConfig,
  AppConfigUpdate,
  AuthResponse,
  Brand,
  BrandLink,
  BulkDeleteResult,
  BulkOwnerResult,
  ChannelTestResult,
  CmsFeatureInfo,
  CmsFeaturePermissions,
  CmsIpRule,
  Device,
  ImportResult,
  LinkCheckResult,
  LinkMatrix,
  LinkReport,
  MyIpInfo,
  Notification,
  NotificationChannel,
  NotificationChannelInput,
  Paginated,
  PermissionsListResponse,
  Role,
  User,
} from "./types";
import { getCountry } from "./country";

/**
 * Đọc API base do build inject.
 *
 * Bun thay thế TĨNH `process.env.BUN_PUBLIC_*` thành chuỗi lúc bundle (cấu hình
 * `[serve.static] env` trong bunfig.toml + cờ `--env` khi build). Hai bẫy:
 *
 * 1. KHÔNG bọc trong `typeof process !== "undefined"` — ở browser `process`
 *    không tồn tại nên guard đó luôn false và giá trị đã inline bị bỏ qua, app
 *    luôn rơi về gateway mặc định.
 * 2. Nếu biến CHƯA được đặt (thiếu `.env`), Bun **giữ nguyên** `process.env.…`
 *    trong bundle ⇒ browser ném `ReferenceError: process is not defined` và
 *    trắng màn hình. Nên phải bọc try/catch — biểu thức vẫn ở dạng literal nên
 *    việc thay thế tĩnh không bị ảnh hưởng.
 */
function envApiBase(): string | undefined {
  try {
    return process.env.BUN_PUBLIC_API_BASE;
  } catch {
    return undefined; // biến chưa đặt lúc build → dùng mặc định bên dưới
  }
}

const BASE = envApiBase() || "https://apigateway.abc667.xyz/api";

/**
 * Origin của backend (bỏ hậu tố `/api`) — Socket.IO gateway nằm cùng origin,
 * path mặc định `/socket.io`, handshake bắt buộc JWT. Dùng cho kết nối realtime.
 */
export const API_ORIGIN = BASE.replace(/\/api\/?$/, "");

const TOKEN_KEY = "acc.token";
const SESSION_KEY = "acc.session_key";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getSessionKey(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setSessionKey(key: string | null) {
  if (key) localStorage.setItem(SESSION_KEY, key);
  else localStorage.removeItem(SESSION_KEY);
}

// ==================== Native WebCrypto Helpers ====================
async function sha256Hex(str: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str || ""));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

async function hmacSha256Hex(keyStr: string, msgStr: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(keyStr),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(msgStr));
  return Array.from(new Uint8Array(sig))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

function normalizeCleanPath(p: string): string {
  if (!p) return "/";
  const withoutQuery = (p.split("?")[0] ?? "").trim();
  const withoutApi = withoutQuery.replace(/^\/api(\/|$)/, "/");
  return withoutApi.startsWith("/") ? withoutApi : "/" + withoutApi;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Nguyên văn thông báo từ server — giữ lại để không mất thông tin gỡ lỗi. */
    public detail?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Phát khi server trả 401 — App bắt để đá về màn hình đăng nhập. */
export const onUnauthorized = new EventTarget();

/**
 * Client này nằm ngoài cây React nên không dùng được hook — I18nProvider đăng ký
 * hàm dịch vào đây để thông báo lỗi cũng đổi theo ngôn ngữ đang chọn.
 */
type Translator = (key: string, vars?: Record<string, string | number>) => string;
let translate: Translator = key => key;

export function setApiTranslator(fn: Translator) {
  translate = fn;
}

/**
 * Backend (NestJS) trả thông báo bằng tiếng Việt cố định, không đổi theo ngôn ngữ
 * người dùng chọn. Ánh xạ những câu đã biết sang khoá i18n; câu lạ giữ nguyên
 * và chỉ hiện như phần chú thích.
 */
const SERVER_MESSAGES: { match: RegExp; key: string }[] = [
  { match: /brand\s*(trống|rỗng)/i, key: "error.brandEmpty" },
  { match: /domain\s*không\s*hợp\s*lệ/i, key: "error.domainInvalid" },
  { match: /link\s*không\s*tồn\s*tại/i, key: "error.linkNotFound" },
  // Vi phạm quyền sở hữu link — cả lỗi 403 lẫn lỗi từng dòng khi import
  { match: /thuộc\s*về\s*user\s*khác/i, key: "error.notOwner" },
];

/** Dịch một thông báo lẻ từ server (dùng cho danh sách lỗi từng dòng khi import). */
export function localizeServerMessage(message: string): string {
  const hit = SERVER_MESSAGES.find(m => m.match.test(message));
  return hit ? translate(hit.key) : message;
}

/** Thông báo theo mã HTTP — luôn đúng ngôn ngữ đang chọn. */
function messageForStatus(status: number, isLogin: boolean): string {
  switch (status) {
    case 400:
      return translate("error.badRequest");
    case 401:
      // Sai mật khẩu lúc đăng nhập khác hẳn với token hết hạn giữa chừng.
      return translate(isLogin ? "error.invalidCredentials" : "error.sessionExpired");
    case 403:
      return translate("error.forbidden");
    case 404:
      return translate("error.notFound");
    case 409:
      return translate("error.conflict");
    case 429:
      return translate("error.tooManyRequests");
    default:
      return status >= 500 ? translate("error.server") : translate("error.generic", { status });
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { raw?: boolean } = {},
): Promise<T> {
  const { raw, ...rest } = init;
  const headers = new Headers(rest.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Quốc gia đang thao tác — API chỉ nghe header này với superadmin, vai trò
  // khác luôn bị khoá vào quốc gia ghi trên tài khoản.
  headers.set("X-Country", getCountry());
  if (rest.body && !(rest.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Header bảo mật bất biến chống lấy trộm token & chống tấn công phát lại (Anti-Replay)
  if (token) {
    const timestamp = Date.now().toString();
    const nonce =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2) + Date.now().toString(36);

    let bodyHash =
      "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"; // sha256('')
    if (rest.body instanceof FormData) {
      bodyHash = "multipart";
    } else if (rest.body) {
      const bodyStr =
        typeof rest.body === "string" ? rest.body : JSON.stringify(rest.body);
      bodyHash = await sha256Hex(bodyStr);
    }

    const cleanPath = normalizeCleanPath(path);
    const method = (rest.method || "GET").toUpperCase();
    const stringToSign = `${method}:${cleanPath}:${timestamp}:${nonce}:${bodyHash}`;

    headers.set("X-Request-Timestamp", timestamp);
    headers.set("X-Request-Nonce", nonce);

    const sessionKey = getSessionKey();
    if (sessionKey) {
      const sig = await hmacSha256Hex(sessionKey, stringToSign);
      headers.set("X-Request-Signature", sig);
    }
  }

  const isLogin = path.startsWith("/auth/login");

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...rest, headers });
  } catch {
    // fetch chỉ reject khi lỗi mạng/CORS — status lỗi vẫn resolve bình thường.
    throw new ApiError(0, translate("error.network"));
  }

  // Sai mật khẩu lúc đăng nhập không phải là mất phiên — đừng bắn sự kiện đăng xuất.
  if (res.status === 401 && !isLogin) {
    setToken(null);
    setSessionKey(null);
    onUnauthorized.dispatchEvent(new Event("unauthorized"));
    throw new ApiError(401, translate("error.sessionExpired"));
  }

  if (!res.ok) {
    let detail: string | undefined;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) detail = body.message.join(", ");
      else if (body.message) detail = body.message;
    } catch {
      // Body không phải JSON — chỉ dùng thông báo theo mã HTTP.
    }

    const localized = detail ? localizeServerMessage(detail) : undefined;
    const base = messageForStatus(res.status, isLogin);
    // Nếu ánh xạ được thì dùng luôn; nếu không, ghép nguyên văn server làm chú thích.
    const message =
      localized && localized !== detail
        ? localized
        : detail
          ? detail.includes("lần thử") ||
            detail.includes("bị khoá") ||
            detail.includes("bị chặn") ||
            detail.includes("thử lại sau")
            ? detail
            : `${base} (${detail})`
          : base;

    throw new ApiError(res.status, message, detail);
  }

  if (raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  auth: {
    login: async (username: string, password: string) => {
      const res = await request<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      if (res.accessToken) setToken(res.accessToken);
      if (res.sessionKey) setSessionKey(res.sessionKey);
      return res;
    },
    logout: async () => {
      try {
        await request<{ ok: boolean }>("/auth/logout", { method: "POST" });
      } catch (_) {}
      setToken(null);
      setSessionKey(null);
    },
    // Đăng ký đã đóng — tài khoản do quản trị viên cấp, không tự đăng ký từ CMS.
    me: () => request<User>("/auth/me"),
  },

  brandLinks: {
    /** `active=false` liệt kê ngược lại — các link đã bị disable (để khôi phục). */
    list: (active = true) => request<BrandLink[]>(`/brand-links?active=${active}`),
    get: (id: number) => request<BrandLink>(`/brand-links/${id}`),
    create: (data: {
      brand: string;
      domain: string;
      expiryAt?: string | null;
      ownerId?: number;
    }) =>
      request<BrandLink>("/brand-links", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (
      id: number,
      data: {
        brand?: string;
        domain?: string;
        expiryAt?: string | null;
        ownerId?: number | null;
      },
    ) =>
      request<BrandLink>(`/brand-links/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    /** Đổi chủ sở hữu cho nhiều link cùng lúc (superadmin/admin). */
    bulkUpdateOwner: (ids: number[], ownerId: number | null) =>
      request<BulkOwnerResult>("/brand-links/owner", {
        method: "PATCH",
        body: JSON.stringify({ ids, ownerId }),
      }),
    remove: (id: number) =>
      request<unknown>(`/brand-links/${id}`, { method: "DELETE" }),
    /** Khôi phục 1 link đã bị disable (`active=true`). */
    restore: (id: number) =>
      request<BrandLink>(`/brand-links/${id}/restore`, { method: "POST" }),
    /** Xoá (disable) nhiều link cùng lúc — chủ sở hữu hoặc admin. */
    bulkRemove: (ids: number[]) =>
      request<BulkDeleteResult>("/brand-links/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    /**
     * Kiểm tra link ngay từ server. Nút gọi hai hàm này hiện đang được COMMENT
     * trên giao diện (BrandLinks.tsx, Dashboard.tsx) — CMS tạm không cho check
     * trực tiếp. Giữ nguyên hàm ở đây để bật lại chỉ cần bỏ comment phần nút.
     */
    check: (id: number) =>
      request<BrandLink>(`/brand-links/${id}/check`, { method: "POST" }),
    checkAll: () =>
      request<BrandLink[]>("/brand-links/check/all", { method: "POST" }),
    exportUrl: (format: "json" | "csv") =>
      `${BASE}/brand-links/export?format=${format}`,
    importJson: (payload: {
      mode?: "upsert" | "skip";
      items?: unknown[];
      csv?: string;
      /** Brand cho mọi dòng không tự ghi brand (ô chọn brand trên form). */
      brand?: string;
    }) =>
      request<ImportResult>("/brand-links/import", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    importFile: (file: File, mode: "upsert" | "skip", brand?: string) => {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      if (brand) form.append("brand", brand);
      return request<ImportResult>("/brand-links/import/file", {
        method: "POST",
        body: form,
      });
    },
  },

  reports: {
    list: (limit = 50) => request<LinkReport[]>(`/reports?limit=${limit}`),
    latest: () => request<LinkReport | null>("/reports/latest"),
    get: (id: number) => request<LinkReport>(`/reports/${id}`),
    generate: () => request<LinkReport>("/reports/generate", { method: "POST" }),
  },

  devices: {
    list: () => request<Device[]>("/devices"),
    get: (id: number) => request<Device>(`/devices/${id}`),
    create: (data: Partial<Device> & { name: string }) =>
      request<Device>("/devices", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<Device>) =>
      request<Device>(`/devices/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id: number) =>
      request<{ deleted: boolean; id: number }>(`/devices/${id}`, {
        method: "DELETE",
      }),
    rotateKey: (id: number) =>
      request<Device>(`/devices/${id}/rotate-key`, { method: "POST" }),
  },

  deviceChecks: {
    list: (params: {
      linkId?: number;
      deviceId?: number;
      region?: string;
      limit?: number;
    }) => {
      const q = new URLSearchParams();
      if (params.linkId != null) q.set("linkId", String(params.linkId));
      if (params.deviceId != null) q.set("deviceId", String(params.deviceId));
      if (params.region) q.set("region", params.region);
      if (params.limit != null) q.set("limit", String(params.limit));
      const qs = q.toString();
      return request<LinkCheckResult[]>(`/device-checks${qs ? `?${qs}` : ""}`);
    },
    /**
     * Không lọc `linkId` thì số bản ghi = số link × số device nên endpoint này có
     * phân trang `limit`/`offset` và trả về `{ items, limit, offset, hasMore }`.
     */
    latest: async (params: { linkId?: number; limit?: number; offset?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.linkId != null) q.set("linkId", String(params.linkId));
      if (params.limit != null) q.set("limit", String(params.limit));
      if (params.offset != null) q.set("offset", String(params.offset));
      const qs = q.toString();
      const res = await request<Paginated<LinkCheckResult> | LinkCheckResult[]>(
        `/device-checks/latest${qs ? `?${qs}` : ""}`,
      );
      // Server cũ trả thẳng mảng — chuẩn hoá để UI chỉ xử lý một dạng.
      return Array.isArray(res)
        ? {
            items: res,
            limit: params.limit ?? res.length,
            offset: params.offset ?? 0,
            hasMore: false,
          }
        : res;
    },
    matrix: (staleMinutes = 60, linkId?: number) => {
      const q = new URLSearchParams();
      q.set("staleMinutes", String(staleMinutes));
      if (linkId != null) q.set("linkId", String(linkId));
      return request<LinkMatrix>(`/device-checks/matrix?${q.toString()}`);
    },
    /**
     * Lệnh chủ động gửi log lỗi của thiết bị check theo khu vực (quốc gia) ra bot Telegram/Lark.
     */
    sendAlert: (params: { country?: string; deviceId?: number; hours?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.country) q.set("country", params.country);
      if (params.deviceId != null) q.set("deviceId", String(params.deviceId));
      if (params.hours != null) q.set("hours", String(params.hours));
      const qs = q.toString();
      return request<{
        ok: boolean;
        country: string;
        sentDevices: number;
        totalBrokenLinks: number;
        message: string;
      }>(`/device-checks/send-alert${qs ? `?${qs}` : ""}`, {
        method: "POST",
      });
    },
    /**
     * Xoá **toàn bộ** bảng lịch sử `link_check_results` (server + device, mọi brand).
     * Chỉ superadmin; server bắt buộc `?confirm=all`, thiếu là `400`. Trả về số dòng đã xoá.
     */
    clearAll: () =>
      request<{ deleted: number }>("/device-checks?confirm=all", { method: "DELETE" }),
  },

  appConfig: {
    // Object thô đầy đủ (kèm updateEnabled/updatedAt) — dùng cho màn hình admin.
    getRaw: () => request<AppConfig>("/app-config/raw"),
    update: (data: AppConfigUpdate) =>
      request<AppConfig>("/app-config", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    /** Upload file APK trực tiếp — server tự ghi đè linkAPK, tối đa 500MB. */
    uploadApk: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<AppConfig>("/app-config/apk", {
        method: "POST",
        body: form,
      });
    },
    apkDownloadUrl: () => `${BASE}/app-config/apk/download`,
  },

  brands: {
    // Chỉ brand của quốc gia đang chọn — header X-Country lo phần lọc
    list: () => request<Brand[]>("/brands"),
    create: (data: { name: string; note?: string }) =>
      request<Brand>("/brands", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: { name?: string; note?: string }) =>
      request<Brand>(`/brands/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id: number) =>
      request<{ deleted: boolean; id: number }>(`/brands/${id}`, {
        method: "DELETE",
      }),
  },

  users: {
    list: () => request<AdminUser[]>("/users"),
    create: (data: {
      username: string;
      password: string;
      role: string;
      brand?: string | null;
    }) => request<AdminUser>("/users", { method: "POST", body: JSON.stringify(data) }),
    update: (
      id: number,
      data: { role?: string; brand?: string | null; disabled?: boolean },
    ) =>
      request<AdminUser>(`/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    setPassword: (id: number, password: string) =>
      request<unknown>(`/users/${id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ password }),
      }),
  },

  notificationChannels: {
    list: (country?: string) => {
      const q = country ? `?country=${encodeURIComponent(country)}` : "";
      return request<NotificationChannel[]>(`/notification-channels${q}`);
    },
    create: (data: NotificationChannelInput) =>
      request<NotificationChannel>("/notification-channels", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: number, data: Partial<NotificationChannelInput>) =>
      request<NotificationChannel>(`/notification-channels/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id: number) =>
      request<{ deleted: boolean; id: number }>(
        `/notification-channels/${id}`,
        { method: "DELETE" },
      ),
    test: (id: number, type?: "default" | "device-batch") => {
      const q = type ? `?type=${encodeURIComponent(type)}` : "";
      return request<ChannelTestResult>(`/notification-channels/${id}/test${q}`, {
        method: "POST",
      });
    },
    testError: (id: number) =>
      request<ChannelTestResult>(`/notification-channels/${id}/test-error`, {
        method: "POST",
      }),
    /** Sao chép kênh sang quốc gia khác — giữ nguyên token/secret bảo mật từ DB. */
    clone: (
      id: number,
      data: {
        targetCountry: string;
        name?: string;
        config?: Record<string, any>;
        enabled?: boolean;
      },
    ) =>
      request<NotificationChannel>(`/notification-channels/${id}/clone`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    /** Liệt kê nhóm Lark mà app đang ở, để chọn chat_id theo tên nhóm. */
    larkChats: (data: {
      appId: string;
      appSecret?: string;
      domain?: string;
      id?: number;
    }) =>
      request<{ chats: { chatId: string; name: string }[] }>(
        "/notification-channels/lark/chats",
        { method: "POST", body: JSON.stringify(data) },
      ),
  },

  notifications: {
    list: (params: { unread?: boolean; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.unread) q.set("unread", "1");
      if (params.limit != null) q.set("limit", String(params.limit));
      const qs = q.toString();
      return request<Notification[]>(`/notifications${qs ? `?${qs}` : ""}`);
    },
    unreadCount: () =>
      request<{ unread: number }>("/notifications/unread-count"),
    readAll: () =>
      request<{ updated: number }>("/notifications/read-all", { method: "POST" }),
    read: (id: number) =>
      request<{ id: number; read: boolean }>(`/notifications/${id}/read`, {
        method: "PATCH",
      }),
  },

  roles: {
    list: () => request<Role[]>("/roles"),
    get: (id: number) => request<Role>(`/roles/${id}`),
    create: (data: { name: string; description?: string; permissions: string[] }) =>
      request<Role>("/roles", { method: "POST", body: JSON.stringify(data) }),
    update: (
      id: number,
      data: { name?: string; description?: string; permissions?: string[] },
    ) =>
      request<Role>(`/roles/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) =>
      request<{ id: number; deleted: boolean }>(`/roles/${id}`, { method: "DELETE" }),
  },

  permissions: {
    list: async (): Promise<CmsFeaturePermissions[]> => {
      const res = await request<CmsFeaturePermissions[] | PermissionsListResponse>(
        "/permissions"
      );
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.features)) return res.features;
      return [];
    },
  },

  ipRules: {
    myIp: () => request<MyIpInfo>("/ip-rules/my-ip"),
    features: () => request<CmsFeatureInfo[]>("/ip-rules/features"),
    list: (filter?: { feature?: string; ruleType?: string; isActive?: boolean }) => {
      const q = new URLSearchParams();
      if (filter?.feature) q.set("feature", filter.feature);
      if (filter?.ruleType) q.set("ruleType", filter.ruleType);
      if (filter?.isActive !== undefined) q.set("isActive", String(filter.isActive));
      const qs = q.toString();
      return request<CmsIpRule[]>(`/ip-rules${qs ? `?${qs}` : ""}`);
    },
    get: (id: number) => request<CmsIpRule>(`/ip-rules/${id}`),
    create: (data: {
      feature: string;
      ruleType: "BLOCK" | "ALLOW";
      ip: string;
      description?: string;
      isActive?: boolean;
    }) =>
      request<CmsIpRule>("/ip-rules", { method: "POST", body: JSON.stringify(data) }),
    update: (
      id: number,
      data: {
        feature?: string;
        ruleType?: "BLOCK" | "ALLOW";
        ip?: string;
        description?: string;
        isActive?: boolean;
      },
    ) =>
      request<CmsIpRule>(`/ip-rules/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) =>
      request<{ id: number; deleted: boolean }>(`/ip-rules/${id}`, { method: "DELETE" }),
  },
  adminLogs: {
    list: (query?: AdminLogsQuery) => {
      const q = new URLSearchParams();
      if (query?.page) q.set("page", String(query.page));
      if (query?.limit) q.set("limit", String(query.limit));
      if (query?.search) q.set("search", query.search);
      if (query?.username) q.set("username", query.username);
      if (query?.ip) q.set("ip", query.ip);
      if (query?.action) q.set("action", query.action);
      if (query?.status) q.set("status", query.status);
      if (query?.method) q.set("method", query.method);
      if (query?.country) q.set("country", query.country);
      if (query?.startDate) q.set("startDate", query.startDate);
      if (query?.endDate) q.set("endDate", query.endDate);
      const qs = q.toString();
      return request<AdminLogsResponse>(`/admin-logs${qs ? `?${qs}` : ""}`);
    },
    get: (id: number) => request<AdminLog>(`/admin-logs/${id}`),
    actions: () => request<string[]>("/admin-logs/actions"),
    stats: () => request<any>("/admin-logs/stats"),
    clean: (days = 30) =>
      request<{ ok: boolean; deleted: number; days: number }>("/admin-logs/clean", {
        method: "DELETE",
        body: JSON.stringify({ days }),
      }),
  },
};

/**
 * Export cần header Authorization nên không dùng được thẻ <a download> trực tiếp —
 * tải bằng fetch rồi tạo blob URL.
 */
export async function downloadExport(format: "json" | "csv") {
  const url = api.brandLinks.exportUrl(format);
  const cleanPath = normalizeCleanPath("/brand-links/export");
  const timestamp = Date.now().toString();
  const nonce =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2) + Date.now().toString(36);
  const bodyHash =
    "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";
  const stringToSign = `GET:${cleanPath}:${timestamp}:${nonce}:${bodyHash}`;

  const headers: Record<string, string> = {
    Authorization: `Bearer ${getToken() ?? ""}`,
    "X-Country": getCountry(),
    "X-Request-Timestamp": timestamp,
    "X-Request-Nonce": nonce,
  };

  const sessionKey = getSessionKey();
  if (sessionKey) {
    headers["X-Request-Signature"] = await hmacSha256Hex(sessionKey, stringToSign);
  }

  const res = await fetch(url, { headers });
  if (!res.ok) throw new ApiError(res.status, translate("error.download", { status: res.status }));
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `brand-links.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}
